// The doctor's own day (Phase 1 quotation G7; API contract §6y): the
// consultation in progress, who is on hold and waiting for this doctor,
// today's appointments, consultations completed today with their note status,
// and lab results released in the last 24 h for this doctor's orders. Shown on
// the Dashboard for the Doctor role; the hospital overview is one click away.
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, CheckCircle2, Clock, FlaskConical, PauseCircle } from 'lucide-react';
import { api } from '@/lib/api/client';
import { branchKey } from '@/lib/queryKeys';
import { KpiTile } from '@/components/ui/KpiTile';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DashCard } from './DashCard';

interface QueueRow {
  queue_id: number; token?: string | null; display_token?: string | null; status: string; priority?: string | null;
  added_at: string; consultation_started_at?: string | null; completed_at?: string | null; held_at?: string | null; hold_reason?: string | null;
  patient_id: number; name: string; uhid: string; age?: number | null; gender?: string | null;
  visit_id?: number | null; visit_code?: string | null; current_stage?: string | null;
  note_approved?: boolean | null; prescriptions?: number;
}
interface AppointmentRow { id: number; start_time: string; end_time: string; status: string; patient_id: number; name: string; uhid: string; queue_status?: string | null }
interface LabRow { order_id: number; order_number: string; patient_id: number; name: string; uhid: string; released_at: string; tests: string | null; has_abnormal: boolean; critical_unacknowledged: boolean }
export interface DoctorDay {
  timezone: string;
  current: QueueRow | null; on_hold: QueueRow[]; waiting: QueueRow[]; completed: QueueRow[];
  appointments: AppointmentRow[]; lab_results: LabRow[];
  counts: { waiting: number; on_hold: number; completed: number; notes_pending: number; appointments: number; appointments_not_arrived: number; lab_results: number };
}

const time = (v?: string | null) => (v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '');
const hhmm = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  if (h == null || m == null) return t;
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const minsSince = (v: string) => Math.max(0, Math.round((Date.now() - new Date(v).getTime()) / 60000));
const who = (r: { name: string; age?: number | null; gender?: string | null }) =>
  [r.name, [r.age != null ? `${r.age}y` : null, r.gender?.[0]].filter(Boolean).join(' ')].filter(Boolean).join(' · ');

export function DoctorDayView({ onOpenVisit, onViewPatientRecord }: {
  onOpenVisit?: (visitCode: string) => void;
  onViewPatientRecord?: (uhid: string) => void;
}) {
  const q = useQuery({
    queryKey: branchKey('doctor-day'),
    queryFn: () => api.get<DoctorDay>('/me/doctor-dashboard'),
    refetchInterval: 30_000,
  });
  if (q.isLoading) return <div className="p-5 text-[13px] text-ink-muted">Loading your day…</div>;
  if (q.error || !q.data) return <div className="p-5 text-[13px] text-danger-ink">Could not load your day: {q.error instanceof Error ? q.error.message : 'unknown error'}</div>;
  const d = q.data;
  const next = d.waiting[0];
  const open = (r: { visit_code?: string | null }) => r.visit_code && onOpenVisit?.(r.visit_code);
  const record = (uhid: string) => onViewPatientRecord?.(uhid);

  return (
    <div className="fade-in">
      <div className="mb-[17px] grid grid-cols-2 gap-[17px] lg:grid-cols-5">
        <KpiTile layout="stacked" tone="orange" icon={<Clock className="h-6 w-6" />} value={d.counts.waiting} label="Waiting for you" sub="In your queue today" />
        <KpiTile layout="stacked" tone="violet" icon={<PauseCircle className="h-6 w-6" />} value={d.counts.on_hold} label="On hold" sub="Paused consultations" />
        <KpiTile layout="stacked" tone="green" icon={<CheckCircle2 className="h-6 w-6" />} value={d.counts.completed} label="Completed today"
          sub={d.counts.notes_pending ? `${d.counts.notes_pending} note${d.counts.notes_pending === 1 ? '' : 's'} not approved` : 'All notes approved'} />
        <KpiTile layout="stacked" tone="blue" icon={<CalendarCheck className="h-6 w-6" />} value={d.counts.appointments} label="Appointments today"
          sub={`${d.counts.appointments_not_arrived} not arrived yet`} />
        <KpiTile layout="stacked" tone="teal" icon={<FlaskConical className="h-6 w-6" />} value={d.counts.lab_results} label="Lab results" sub="Released in the last 24 h" />
      </div>

      <div className="mb-4 rounded-[var(--radius)] border border-line bg-card px-[18px] py-4 shadow-card">
        {d.current ? (
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="info">In consultation</Badge>
            <span className="text-[14px] font-semibold text-ink">{who(d.current)}</span>
            <span className="text-[12px] text-ink-muted">Token {d.current.display_token || d.current.token} · since {time(d.current.consultation_started_at)}</span>
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => record(d.current!.uhid)}>Patient record</Button>
              <Button size="sm" onClick={() => open(d.current!)} disabled={!d.current.visit_code}>Resume consultation →</Button>
            </span>
          </div>
        ) : next ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[13px] text-ink-muted">Next patient:</span>
            <span className="text-[14px] font-semibold text-ink">{who(next)}</span>
            {next.priority === 'Emergency' && <Badge variant="danger">Emergency</Badge>}
            <span className="text-[12px] text-ink-muted">Token {next.display_token || next.token} · waiting {minsSince(next.added_at)} min</span>
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => record(next.uhid)}>Patient record</Button>
              <Button size="sm" onClick={() => open(next)} disabled={!next.visit_code}>Open visit →</Button>
            </span>
          </div>
        ) : (
          <div className="text-[13px] text-ink-muted">No one is waiting for you right now.</div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DashCard title="Your queue">
          {[...d.on_hold, ...d.waiting].length === 0 ? <Empty text="No patients waiting or on hold." /> : (
            <ul className="mt-2 flex list-none flex-col divide-y divide-line p-0">
              {[...d.on_hold, ...d.waiting].map((r) => (
                <li key={r.queue_id} className="flex flex-wrap items-center gap-2 py-2 text-[12.5px]">
                  <span className="w-14 font-mono text-ink-muted">{r.display_token || r.token}</span>
                  <span className="min-w-0 flex-1 truncate text-ink">{who(r)}</span>
                  {r.status === 'On Hold' ? <Badge variant="warn">On hold{r.hold_reason ? ` · ${r.hold_reason}` : ''}</Badge>
                    : <span className="text-ink-muted">{minsSince(r.added_at)} min</span>}
                  {r.priority === 'Emergency' && <Badge variant="danger">Emergency</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => open(r)} disabled={!r.visit_code}>Open</Button>
                </li>
              ))}
            </ul>
          )}
        </DashCard>

        <DashCard title="Today's appointments">
          {d.appointments.length === 0 ? <Empty text="No appointments booked with you today." /> : (
            <ul className="mt-2 flex list-none flex-col divide-y divide-line p-0">
              {d.appointments.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-2 py-2 text-[12.5px]">
                  <span className="w-20 text-ink-muted">{hhmm(a.start_time)}</span>
                  <span className="min-w-0 flex-1 truncate text-ink">{a.name}</span>
                  {a.queue_status === 'Completed' || a.status === 'completed' ? <Badge variant="success">Seen</Badge>
                    : a.queue_status ? <Badge variant="info">Checked in</Badge>
                      : <Badge variant="neutral">Not arrived</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => record(a.uhid)}>Record</Button>
                </li>
              ))}
            </ul>
          )}
        </DashCard>

        <DashCard title="Completed today">
          {d.completed.length === 0 ? <Empty text="No consultations completed yet today." /> : (
            <ul className="mt-2 flex list-none flex-col divide-y divide-line p-0">
              {d.completed.map((r) => (
                <li key={r.queue_id} className="flex flex-wrap items-center gap-2 py-2 text-[12.5px]">
                  <span className="w-20 text-ink-muted">{time(r.completed_at)}</span>
                  <span className="min-w-0 flex-1 truncate text-ink">{who(r)}</span>
                  {r.note_approved ? <Badge variant="success">Note approved</Badge> : <Badge variant="warn">Note not approved</Badge>}
                  {(r.prescriptions ?? 0) > 0 && <Badge variant="neutral">Rx ×{r.prescriptions}</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => record(r.uhid)}>Record</Button>
                </li>
              ))}
            </ul>
          )}
        </DashCard>

        <DashCard title="Lab results for your patients">
          {d.lab_results.length === 0 ? <Empty text="No results released in the last 24 hours." /> : (
            <ul className="mt-2 flex list-none flex-col divide-y divide-line p-0">
              {d.lab_results.map((l) => (
                <li key={l.order_id} className="flex flex-wrap items-center gap-2 py-2 text-[12.5px]">
                  <span className="min-w-0 flex-1">
                    <span className="text-ink">{l.name}</span>
                    <span className="block truncate text-[11.5px] text-ink-muted">{l.order_number} · {l.tests} · {time(l.released_at)}</span>
                  </span>
                  {l.critical_unacknowledged ? <Badge variant="danger">Critical — not acknowledged</Badge>
                    : l.has_abnormal ? <Badge variant="warn">Abnormal</Badge> : <Badge variant="success">Normal</Badge>}
                  <Button size="sm" variant="ghost" onClick={() => record(l.uhid)}>Record</Button>
                </li>
              ))}
            </ul>
          )}
        </DashCard>
      </div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="py-6 text-center text-[12.5px] text-ink-muted">{text}</div>;
}
